import { inject, Injectable } from "@angular/core";
import { Http } from "@skylab-angular-shared/core";
import { ConsoleLogger, HttpService } from "@skylab-shared/core";
import { AxiosError, HttpStatusCode } from "axios";

export type AuthUnauthorizedResponse = {
	unauthorized: true;
}

export type AuthMeResponse = {
    userId: number
    userCorrelationId: string;
    name: string;
}

export type AuthTokenResponse = {
    userCorrelationId: string,
    tenantCorrelationId: string,
    email: string,
    firstName: string,
    surname: string,
    companyId: number,
    companyIdentityId: number,
    companyName: string,
    accountRoleId: number
};

export type AuthGetCompanyContextResponse = {
    userCorrelationId: string,
    tenantCorrelationId: string,
    email: string,
    firstName: string,
    surname: string,
    companyId: number,
    companyIdentityId: number,
    companyName: string,
    accountRoleId: number
};

export type AuthCompanyContextRequestData = {
	contextId?: string;
	companyIdentityId?: string;
}

@Injectable({providedIn: 'root'})
export class AuthService {
	#http = inject(Http) as HttpService;
	#baseAuthUrl = 'auth';
	#logger = new ConsoleLogger(this.constructor.name);

	public async provideTokenAsync(params: {state: string, code: string, contextId: string}): Promise<AuthTokenResponse | AuthUnauthorizedResponse> {
		try {
			const response = await this.#http.post<AuthTokenResponse>(`${this.#baseAuthUrl}/token?state=${params.state}&code=${params.code}&contextId=${params.contextId || ''}`);
			return response.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.status === HttpStatusCode.Unauthorized) {
				return { unauthorized: true }
			} else {
				throw new Error(`Authorization Error ${err}`)
			}
		}
	}

	public async getAccountInfoAsync(): Promise<AuthMeResponse | AuthUnauthorizedResponse> {
		try {
			const response = await this.#http.get<AuthMeResponse>(`${this.#baseAuthUrl}/me`);
			return response.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.status === HttpStatusCode.Unauthorized) {
				return { unauthorized: true }
			} else {
				throw new Error(`Authorization Error ${err}` )
			}
		}
	}

	public async getCompanyContext(params: AuthCompanyContextRequestData): Promise<AuthGetCompanyContextResponse | AuthUnauthorizedResponse> {
		try {
			const requestData: AuthCompanyContextRequestData = {};
			if (params.companyIdentityId) requestData.companyIdentityId = params.companyIdentityId;
			if (params.contextId) requestData.contextId = params.contextId;

			const response = await this.#http.post<AuthGetCompanyContextResponse>(`${this.#baseAuthUrl}/getCompanyContext`, requestData);
			return response.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.status === HttpStatusCode.Unauthorized) {
				return { unauthorized: true }
			} else {
				throw new Error(`Authorization Error ${err}` )
			}
		}
	}

	public async removeCookiesAsync(): Promise<{status: 'Success' | 'Failed'}> {
		try {
			await this.#http.get<AuthGetCompanyContextResponse>(`${this.#baseAuthUrl}/remove-cookies`);
			return { status: 'Success' };
		} catch (err) {
			this.#logger.error('Removing Cookies Error');
			return { status: 'Failed' };
		}
	}

	public redirectToLoginPage(): void {
		window.location.href = `gw/${this.#baseAuthUrl}/login?returnUrl=${window.location.href}`
	}

	public logout(): void {
		window.location.href = `gw/${this.#baseAuthUrl}/logout`
	}
}